import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  Loader2,
  RefreshCw,
  Book,
  Code,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";
import { automationApi, SkillSummary } from "@/services/api/automation";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/skills")({
  component: SkillsCatalog,
});

interface SkillDetail extends SkillSummary {
  content?: string;
  content_location?: string;
}

function SkillsCatalog() {
  const { token, session } = useAuth();
  usePortalData();
  const canSync = isSuperAdmin(session);
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<SkillDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const loadSkills = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const params =
        selectedCategory !== "all" ? { category: selectedCategory } : undefined;
      const response = await automationApi.skills.list(token, params);
      setSkills(response.skills);
      setCategories(response.categories);
    } catch (error) {
      console.error("Failed to load skills:", error);
      toast.error("Failed to load skills catalog");
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategory]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const handleSync = async () => {
    if (!token) return;
    try {
      setSyncing(true);
      const response = await automationApi.skills.sync(token);
      toast.success(`Synced ${response.synced_count} skills`);
      await loadSkills();
    } catch (error) {
      console.error("Failed to sync skills:", error);
      toast.error("Failed to sync skills from filesystem");
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetail = async (skillId: string) => {
    if (!token) return;
    try {
      setLoadingDetail(true);
      setDetailOpen(true);
      const response = await automationApi.skills.get(token, skillId);
      setSelectedSkill(response as SkillDetail);
    } catch (error) {
      console.error("Failed to load skill detail:", error);
      toast.error("Failed to load skill details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatCategoryName = (category: string) => {
    return category
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Sort + paginate skills
  const { sortedData: sortedSkills, requestSort, getSortDirection } =
    useSortableData(skills, {
      initialSort: { key: "name", direction: "asc" },
    });

  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData({
    data: sortedSkills as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["name", "description", "category"],
    searchTerm,
  });
  const displaySkills = paginatedData as unknown as SkillSummary[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Skills Catalog</h1>
          <p className="mt-2 text-lg text-slate-600">
            View available agent skills synced from GitHub
          </p>
        </div>
        {canSync && (
          <Button className="rounded-full" onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync Now
          </Button>
        )}
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search skills..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {formatCategoryName(cat)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadSkills} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displaySkills.length === 0 ? (
          <div className="text-center py-12">
            <Book className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No skills match your search" : "No skills available"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm ? "Try a different search term." : "Skills will appear here once synced from GitHub."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead direction={getSortDirection("name")} onSort={() => requestSort("name")}>Name</SortableTableHead>
                <SortableTableHead direction={getSortDirection("category")} onSort={() => requestSort("category")}>Category</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>Description</SortableTableHead>
                <SortableTableHead direction={getSortDirection("version")} onSort={() => requestSort("version")}>Version</SortableTableHead>
                <SortableTableHead direction={getSortDirection("scope")} onSort={() => requestSort("scope")}>Scope</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>Task Types</SortableTableHead>
                <SortableTableHead direction={getSortDirection("is_active")} onSort={() => requestSort("is_active")}>Status</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displaySkills.map((skill) => (
                <TableRow
                  key={skill.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleViewDetail(skill.id)}
                >
                  <TableCell className="font-medium">{skill.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{formatCategoryName(skill.category)}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[300px]">
                    <span className="text-sm text-muted-foreground line-clamp-1">
                      {skill.description || "No description"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">v{skill.version}</Badge>
                  </TableCell>
                  <TableCell>
                    {skill.scope !== "global" ? (
                      <Badge variant="default" className="text-xs">{skill.scope}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">global</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {skill.task_types.slice(0, 2).map((type) => (
                        <Badge key={type} variant="secondary" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                      {skill.task_types.length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{skill.task_types.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {skill.is_active ? (
                      <Badge variant="success" className="text-xs">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="text-xs">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalItems}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={currentPage > 1}
              canGoNext={currentPage < totalPages}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
              onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            />
          </div>
        )}
      </div>

      {/* Skill Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {loadingDetail
                ? "Loading..."
                : selectedSkill?.name || "Skill Details"}
            </DialogTitle>
            {selectedSkill && !loadingDetail && (
              <DialogDescription>{selectedSkill.description}</DialogDescription>
            )}
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : selectedSkill ? (
            <>
              <div className="flex items-center gap-2 -mt-2">
                <Badge variant="outline">v{selectedSkill.version}</Badge>
                {selectedSkill.is_active ? (
                  <Badge variant="success">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="h-3 w-3 mr-1" />
                    Inactive
                  </Badge>
                )}
              </div>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Category
                    </h4>
                    <Badge>{formatCategoryName(selectedSkill.category)}</Badge>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Scope
                    </h4>
                    <Badge variant="outline">{selectedSkill.scope}</Badge>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">
                    Task Types
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedSkill.task_types.map((type) => (
                      <Badge key={type} variant="secondary">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm text-muted-foreground mb-2">
                    Capabilities
                  </h4>
                  <div className="flex flex-wrap gap-1">
                    {selectedSkill.capabilities.map((cap) => (
                      <Badge key={cap} variant="outline">
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>

                {selectedSkill.content && (
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2 flex items-center gap-2">
                      <Code className="h-4 w-4" />
                      Skill Content
                    </h4>
                    <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
                      <pre className="text-sm whitespace-pre-wrap break-all font-mono overflow-x-auto">
                        {selectedSkill.content}
                      </pre>
                    </div>
                  </div>
                )}

                {selectedSkill.content_location && (
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium">Location:</span>{" "}
                    {selectedSkill.content_location}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
